import { AppRoutes } from '@constants';

export const RESET_TOKEN_STORAGE_KEY = 'resetToken';
const TOKEN_PARAM = 'token';

/**
 * Moves the reset token from the URL into sessionStorage before the app (and
 * GA) boots. With the token gone from `document.location`, no analytics hit —
 * including GA4 enhanced measurement (`scroll`, `form_start`, history page
 * views) — can carry it. sessionStorage survives a reload of the same tab, so
 * reloading the reset page keeps working. A boot on any other route drops a
 * stashed token, so leaving the page by a full load cannot leave it behind.
 */
export function stashResetTokenFromUrl(
  location: Location = window.location,
  history: History = window.history,
  storage: Storage = window.sessionStorage,
): void {
  if (location.pathname !== `/${AppRoutes.CREATE_NEW_PASSWORD}`) {
    clearStashedResetToken(storage);
    return;
  }
  const params = new URLSearchParams(location.search);
  const token = params.get(TOKEN_PARAM);
  if (token === null) {
    return;
  }
  try {
    storage.setItem(RESET_TOKEN_STORAGE_KEY, token);
  } catch {
    // Storage blocked: keep the token in the URL so the link still works.
    return;
  }
  params.delete(TOKEN_PARAM);
  const query = params.toString();
  history.replaceState(
    history.state,
    '',
    `${location.pathname}${query ? `?${query}` : ''}${location.hash}`,
  );
}

export function readStashedResetToken(
  storage: Storage = window.sessionStorage,
): string {
  try {
    return storage.getItem(RESET_TOKEN_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function clearStashedResetToken(
  storage: Storage = window.sessionStorage,
): void {
  try {
    storage.removeItem(RESET_TOKEN_STORAGE_KEY);
  } catch {
    // Nothing stored when storage is blocked.
  }
}
